import { memo, useMemo, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { ProductCard } from '@/features/shop/components/ProductCard'
import { ProductEndcap } from '@/features/shop/components/ProductEndcap'
import { LazyLoadObserve } from '@/features/shared/components/ScrollIntoView'
import {
  PRODUCT_GRID_TYPE,
  ENDCAP_GRID_TYPE,
  EMPTY_GRID_TYPE,
  AD_SHOWCASE_GRID_TYPE,
  SHELF_GRID_TYPE,
  COMPLEMENTARY_SHELF_GRID_TYPE,
} from '@/features/shared/constants/productGridTypes'
import { generateGrid } from '@/features/shop/components/ProductGrid/generateGrid'
import { useCalculateGridColumns } from '@/features/shop/components/ProductGrid/useCalculateGridColumns'
import { useOnProductGrid } from '@/features/shared/hooks/useOnProductGrid'
import { findCurrentCategoryByParams } from '@/features/shop/services/Search/utils'
import { CouponsContextProvider } from '@/features/shop/components/Coupons/CouponsContext'
import { screenSizes } from '@shipt/design-system-themes'
import {
  type OnProductArgs,
  type OnQtyArgs,
  type RenderQuickAdd,
} from '@/features/shop/components/ProductCard/types'
import { MIN_HEIGHT } from '@shared/constants/productCard'
import { SearchAdShowcaseUnit } from '@/features/cms/components/CMS/components/Shelves/SearchAdVideoShowcaseUnit'
import { BuyAgainProductShelf } from '@/features/shop/components/Shelf/BuyAgainProductShelf'
import { useUpdateProductQuantity } from '@/features/shared/hooks/useUpdateProductQuantity'
import { ComplementaryProductShelf } from '@/features/cms/components/CMS/components/Shelves/ProductShelf/ComplementaryProductShelf'
import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import { complementaryShelfStateAtom } from '@/features/shop/state/ComplementaryShelf/atoms'
import { AnimatePresence, m } from 'framer-motion'
import {
  useFetchComplementaryShelf,
  useComplementaryShelfExperiment,
} from '@/features/shop/state/ComplementaryShelf/hooks'
import { useValidSearchParams } from '@/features/shared/hooks/search'
import { type AdShowcaseData, type AdVideoShowcaseData } from '@/features/cms/components/CMS/types'
import { type SearchResults } from '@/features/shop/services/Search/types'
import { type ShoppingListWithProduct } from '@/features/shop/services/ShoppingLists/types'
import { useBasket } from '@/features/shared/hooks/useBasket'

type ProductGridProps = {
  searchResults: Pick<SearchResults, 'coupons' | 'products' | 'facets' | 'beacons'>
  className?: string
  showSeeSimilar?: boolean
  setNextPage?: () => void
  usualsList?: ShoppingListWithProduct
  renderQuickAdd?: RenderQuickAdd
  onProductAddedAction?: () => void
  onProductClickedAction?: (args?: OnProductArgs, e?: React.MouseEvent<HTMLAnchorElement>) => void
  showLargerProducts?: boolean
  showAddToListButton?: boolean
  adShowcase?: AdShowcaseData | AdVideoShowcaseData
  displaySublocation?: string
  hideRatings?: boolean
  isSearchGrid?: boolean
  showBuyAgainShelf?: boolean
}

const EAGER_LOAD_LIMIT = 12
export const ProductGridRenderer = memo(
  ({
    searchResults,
    showSeeSimilar,
    className,
    setNextPage,
    usualsList,
    renderQuickAdd,
    onProductAddedAction,
    onProductClickedAction,
    showLargerProducts = false,
    showAddToListButton = false,
    displaySublocation,
    hideRatings,
    isSearchGrid = false,
    adShowcase,
    showBuyAgainShelf,
  }: ProductGridProps) => {
    const searchParams = useValidSearchParams()
    const { updateProductQuantity } = useUpdateProductQuantity()
    const { getProductQuantity, getProductPromotionMetadata } = useBasket()

    const { products, facets = [], coupons } = searchResults

    const hasSearchTerm = isSearchGrid && Boolean(searchParams.query)
    const { isEnabled: isComplementaryShelfEnabled } =
      useComplementaryShelfExperiment(hasSearchTerm)
    const fetchComplementaryShelf = useFetchComplementaryShelf(hasSearchTerm)
    const { shelfDisplayData, hasDismissed: hasDismissedComplementaryShelf } = useAtomValue(
      complementaryShelfStateAtom,
    )
    const setComplementaryShelfState = useSetAtom(complementaryShelfStateAtom)
    const resetComplementaryShelf = useResetAtom(complementaryShelfStateAtom)

    // The complementary shelf needs to be cleared if a user modifies these rendered filters
    const filterParams = useMemo(
      () => ({
        sort: searchParams.sort,
        brands: searchParams.brands,
        active_deals: searchParams.active_deals,
        flavors: searchParams.flavors,
        category_id: searchParams.category_id,
      }),
      [
        searchParams.sort,
        searchParams.brands,
        searchParams.active_deals,
        searchParams.flavors,
        searchParams.category_id,
      ],
    )

    const prevFilterParamsRef = useRef(filterParams)
    const isComplementaryShelfLoaded = shelfDisplayData !== null
    const complementaryShelfRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (isComplementaryShelfEnabled && searchParams.query) {
        resetComplementaryShelf()
      }
    }, [isComplementaryShelfEnabled, searchParams.query, resetComplementaryShelf])

    useEffect(() => {
      if (!isComplementaryShelfEnabled) return

      const filtersChanged = prevFilterParamsRef.current !== filterParams

      if (isComplementaryShelfLoaded && filtersChanged) {
        setComplementaryShelfState((prev) => ({
          ...prev,
          shelfDisplayData: null,
        }))
      }

      prevFilterParamsRef.current = filterParams
    }, [
      isComplementaryShelfLoaded,
      isComplementaryShelfEnabled,
      filterParams,
      setComplementaryShelfState,
    ])

    const { onQtyAdded, onQtyRemoved, onProductClick, onProductInView } = useOnProductGrid({
      displaySublocation,
      ...(facets.length > 0 && {
        currentCategory: findCurrentCategoryByParams(facets, searchParams),
      }),
    })

    const onProductClicked = useCallback(
      (args: OnProductArgs, e: React.MouseEvent<HTMLAnchorElement>) => {
        onProductClick(args)
        onProductClickedAction?.(args, e)
      },
      [onProductClick, onProductClickedAction],
    )

    const { gridColumns, getProductGridRef, getProductCardRef } = useCalculateGridColumns()

    const grid = useMemo(() => {
      return generateGrid({
        gridColumns,
        searchResults,
        adShowcase,
        showBuyAgainShelf,
        isSearchGrid,
        complementaryShelf: shelfDisplayData?.shelfData,
        complementaryShelfProductIndex: shelfDisplayData?.productIndex,
      })
    }, [gridColumns, searchResults, adShowcase, showBuyAgainShelf, isSearchGrid, shelfDisplayData])

    // this map represents the index of products in the grid vs the index of products in the products array.
    // this is needed in order to know which product's complementary shelf to fetch when a product is added
    // to cart, as the grid may contain endcaps and ad showcases that take up grid spaces but are not products.
    const productIndexMap = useMemo(() => {
      const map = new Map<number, number>()
      let count = 0
      grid.forEach((card, gridIndex) => {
        if (card.renderType === PRODUCT_GRID_TYPE) {
          map.set(gridIndex, count++)
        }
      })
      return map
    }, [grid])

    const onComplementaryShelfFetch = useCallback(
      (productId: number, gridIndex: number, productName: string) => {
        const productIndex = productIndexMap.get(gridIndex) ?? 0
        fetchComplementaryShelf(productId, productIndex, productName)
      },
      [fetchComplementaryShelf, productIndexMap],
    )

    const onProductAdded = useCallback(
      ({ product, itemUpdated, index }: OnQtyArgs) => {
        onQtyAdded({ product, itemUpdated, index })
        onProductAddedAction?.()

        onComplementaryShelfFetch(product.id, index, product.name)
      },
      [onQtyAdded, onProductAddedAction, onComplementaryShelfFetch],
    )

    return (
      <CouponsContextProvider coupons={coupons}>
        <GridUL ref={getProductGridRef} className={className}>
          {grid.map((card, gridIndex) => {
            // this variable is just to bypass the eslint rule that complains about using index
            // as key, but this is fine because we want to use gridIndex as part of the key.
            const orderIndex = gridIndex
            const eagerLoad = gridIndex < EAGER_LOAD_LIMIT
            switch (card.renderType) {
              case PRODUCT_GRID_TYPE:
                const { data: product } = card
                return (
                  <ProductCardListItem
                    ref={(node) => {
                      getProductCardRef(node, gridIndex)
                    }}
                    key={`product-${product.id}-${orderIndex}`}
                    // order changes frequently; a static prop would generate excessive SC classes - move back to a prop post-Linaria https://app.clickup.com/t/36127442/86bbkknge
                    // oxlint-disable-next-line react/forbid-component-props
                    style={{ order: gridIndex }}
                  >
                    <ProductCardLazyLoadWrapper
                      minHeight={MIN_HEIGHT}
                      eagerLoad={eagerLoad}
                      onInView={() => {
                        // trigger next page load when the last product
                        // comes into view if the page is using infinite scroll
                        if (products?.at(-1)?.id === product.id) setNextPage?.()
                      }}
                    >
                      <ProductCard
                        product={product}
                        onQtyAdded={onProductAdded}
                        onQtyRemoved={onQtyRemoved}
                        onClick={onProductClicked}
                        onInView={onProductInView}
                        index={gridIndex}
                        showSeeSimilar={showSeeSimilar}
                        eagerLoad={eagerLoad}
                        renderQuickAdd={renderQuickAdd}
                        usualsList={usualsList}
                        showLargerProducts={showLargerProducts}
                        showAddToListButton={showAddToListButton}
                        updateProductQuantity={updateProductQuantity}
                        hideRatings={hideRatings}
                        onComplementaryShelfFetch={onComplementaryShelfFetch}
                        productQuantity={getProductQuantity(product)}
                        promotionMetadata={getProductPromotionMetadata(product)}
                        linkToUniversalPDP={false}
                      />
                    </ProductCardLazyLoadWrapper>
                  </ProductCardListItem>
                )
              case ENDCAP_GRID_TYPE:
                const { data: endcap } = card
                if (!endcap) return null
                return (
                  <EndcapListItem
                    slotSize={endcap.slot_size}
                    key={`endcap-${endcap.promotion_id}-${endcap.id}`}
                    showLargerProducts={showLargerProducts}
                    // order changes frequently; a static prop would generate excessive SC classes - move back to a prop post-Linaria https://app.clickup.com/t/36127442/86bbkknge
                    // oxlint-disable-next-line react/forbid-component-props
                    style={{ order: gridIndex }}
                  >
                    <ProductCardLazyLoadWrapper eagerLoad={eagerLoad} minHeight={MIN_HEIGHT}>
                      <ProductEndcap gridIndex={gridIndex} endcap={endcap} />
                    </ProductCardLazyLoadWrapper>
                  </EndcapListItem>
                )
              case AD_SHOWCASE_GRID_TYPE: {
                if (!adShowcase) return null
                const cmsProductsLength = adShowcase.data?.items?.length ?? 0

                const isFullWidth =
                  adShowcase.data?.version === 'v2' ||
                  adShowcase.content_type_id === 'ad_video_showcase'

                return (
                  <ShelfContainer
                    key={`featured-ad-shelf-${adShowcase.name}`}
                    showLargerProducts={showLargerProducts}
                    slotSize={cmsProductsLength < 3 ? cmsProductsLength + 1 : 4}
                    productLength={cmsProductsLength}
                    isFullWidth={isFullWidth}
                    // order changes frequently; a static prop would generate excessive SC classes - move back to a prop post-Linaria https://app.clickup.com/t/36127442/86bbkknge
                    // oxlint-disable-next-line react/forbid-component-props
                    style={{ order: gridIndex }}
                  >
                    <SearchAdShowcaseUnit
                      index={gridIndex}
                      showLargerProducts={showLargerProducts}
                      {...adShowcase}
                    />
                  </ShelfContainer>
                )
              }
              case SHELF_GRID_TYPE: {
                const shelf = card.data
                if (!shelf || !showBuyAgainShelf || !isSearchGrid) return null

                return (
                  <FullRowListItem
                    key={`shelf-${shelf.type}-${orderIndex}`}
                    showLargerProducts={showLargerProducts}
                    slotSize={gridColumns}
                    // order changes frequently; a static prop would generate excessive SC classes - move back to a prop post-Linaria https://app.clickup.com/t/36127442/86bbkknge
                    // oxlint-disable-next-line react/forbid-component-props
                    style={{ order: gridIndex }}
                  >
                    <BuyAgainProductShelf shelf={shelf} shelfIndex={gridIndex} />
                  </FullRowListItem>
                )
              }
              case COMPLEMENTARY_SHELF_GRID_TYPE: {
                const shelf = card.data
                const addedProductName = shelfDisplayData?.productName

                if (!shelf) return null

                return (
                  <AnimatePresence key="complementary-shelf-presence">
                    {!hasDismissedComplementaryShelf && (
                      <FullRowListItem
                        role="complementary"
                        aria-label={
                          addedProductName
                            ? `Often bought with ${addedProductName}`
                            : 'Complementary products shelf'
                        }
                        key={`complementary-shelf-${shelf.id}-${orderIndex}`}
                        showLargerProducts={showLargerProducts}
                        slotSize={gridColumns}
                        // order changes frequently; a static prop would generate excessive SC classes - move back to a prop post-Linaria https://app.clickup.com/t/36127442/86bbkknge
                        // oxlint-disable-next-line react/forbid-component-props
                        style={{ order: gridIndex }}
                        /* animation props */
                        as={m.li}
                        initial={{ height: 0, margin: 0 }}
                        animate={{ height: 'auto', margin: LIST_ITEM_MARGIN }}
                        transition={{ duration: 0.3 }}
                        exit={{ height: 0, margin: 0 }}
                        onAnimationComplete={() => {
                          if (complementaryShelfRef.current) {
                            const rect = complementaryShelfRef.current.getBoundingClientRect()
                            const isBottomBelowViewport = rect.bottom > window.innerHeight
                            if (isBottomBelowViewport) {
                              complementaryShelfRef.current.scrollIntoView({
                                behavior: 'smooth',
                                block: 'end',
                              })
                            }
                          }
                        }}
                        ref={complementaryShelfRef}
                      >
                        <ComplementaryProductShelf
                          shelf={shelf}
                          index={gridIndex}
                          handleClose={() =>
                            setComplementaryShelfState((prev) => ({
                              ...prev,
                              hasDismissed: true,
                            }))
                          }
                        />
                      </FullRowListItem>
                    )}
                  </AnimatePresence>
                )
              }
              case EMPTY_GRID_TYPE:
                return (
                  <EmptyGridListItem
                    key={`empty-${orderIndex}`}
                    // order changes frequently; a static prop would generate excessive SC classes - move back to a prop post-Linaria https://app.clickup.com/t/36127442/86bbkknge
                    // oxlint-disable-next-line react/forbid-component-props
                    style={{ order: gridIndex }}
                  />
                )
              default:
                return null
            }
          })}
        </GridUL>
      </CouponsContextProvider>
    )
  },
)

type ItemType = {
  slotSize: number
  showLargerProducts: boolean
  isFullWidth?: boolean
}

/**
 * This ensures that on initial load, all divs have a minimum height so
 * that only the first few rows are above the fold. If this is not in
 * place, the initial height of elements are 0, and therefore all are in
 * view and all are loading on initial render.
 */
const ProductCardLazyLoadWrapper = styled(LazyLoadObserve)`
  height: 100%;
  width: 100%;
`

const GRID_PRODUCT_CARD_WIDTH = 154
const listItemWidth = GRID_PRODUCT_CARD_WIDTH + 24

const GridUL = styled.ul`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  list-style-type: none;
  margin: 0;
  padding: 0;

  /* Ideally the product grid would not have any padding. Adding this until we can
   refactor the product grid to use CSS Grid in order to keep things aligned. */
  &.searchProductsRender {
    grid-column: full-width;

    @media ${screenSizes.tablet} {
      grid-column: content;
    }
  }
`

// This is pulled out as a constant for use in the animation of the complementary shelf
const LIST_ITEM_MARGIN = '0.5rem 0 1rem'

const ListItem = styled.li`
  padding: 0 11px;
  margin: ${LIST_ITEM_MARGIN};
`

const FullRowListItem = styled(ListItem)<ItemType>`
  flex: 1 0 100%;

  /* allows the wrapper to clip the height without clipping the carousel nav buttons
  when animating the complementary shelf */
  overflow-y: clip;

  @media ${screenSizes.max_mobile} {
    padding: 0;
  }
`

const ProductCardListItem = styled(ListItem)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  min-width: 0;
  flex: 1 ${listItemWidth}px;
`

const EndcapListItem = styled(ListItem)<ItemType>`
  cursor: pointer;
  flex: ${({ slotSize }) => `${slotSize} ${listItemWidth * slotSize}px`};
`

const EmptyGridListItem = styled(ListItem)`
  margin-bottom: 0;
  flex: 1 ${listItemWidth}px;
`

const ShelfContainer = styled(ListItem)<ItemType & { productLength: number }>`
  margin: ${({ isFullWidth }) => (isFullWidth ? '0.5rem 0 0.5rem' : '0.5rem 0 1rem')};
  flex: ${({ isFullWidth, slotSize }) =>
    isFullWidth ? '0 0 100%' : `2 ${listItemWidth * slotSize}px`};
  width: ${({ isFullWidth }) => isFullWidth && '100%'};

  @media ${screenSizes.max_mobile} {
    padding-left: 0;
    padding-right: 0;
  }

  @media ${screenSizes.tablet} {
    flex: ${({ isFullWidth, slotSize }) =>
      isFullWidth ? '0 0 100%' : `${slotSize} ${listItemWidth * slotSize}px`};
  }

  @media ${screenSizes.smDesktop} {
    width: ${({ showLargerProducts, isFullWidth }) => showLargerProducts && !isFullWidth && '100%'};
  }

  @media ${screenSizes.lgDesktop} {
    width: ${({ showLargerProducts, isFullWidth }) =>
      showLargerProducts && !isFullWidth && 'fit-content'};
  }
`
